<template>
  <div>
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <a :href="baseUrl">{{ this.shopName }}</a></el-breadcrumb-item
      >
      <el-breadcrumb-item :to="{ path: $routePage.EC_LINE001 }">{{
        $t('ec-line001.content.listOfProductsHandled')
      }}</el-breadcrumb-item>
      <el-breadcrumb-item>{{ data.name }}</el-breadcrumb-item>
    </el-breadcrumb>
    <product-details
      :data="data"
      :items="items"
      :sku-item-selection.sync="skuSelected"
      :sale-type-selection.sync="saleTypeSelected"
      :label-submit="$t('common.action.addToCart')"
      :label-route="$t('ec-line002.content.routeBtn')"
      v-loading="loading"
      @submit="onSubmit"
    >
      <template #price>
        <div class="flex flex-col">
          <i18n path="common.includeTax">
            <template #num>
              <span class="text-xl">{{ $n(productPrice(), 'currency', 'ja-JP') }} </span>
            </template>
          </i18n>

          <span v-if="Number(skuSelected.price01_after_tax)" class="text-xs leading-3 font-bold mt-2">{{
            $t('common.listedPrice', { num: $n(Number(skuSelected.price01_after_tax), 'currency', 'ja-JP') })
          }}</span>
        </div>
      </template>
    </product-details>
  </div>
</template>

<script>
import ProductDetails from '@/components/ec-line002/ProductDetails.vue'
import { ProductService } from '@/services'
import BreadcrumbMixin from '@/mixins/breadcrumb'
import { ENV_KEY } from '@/constants/env'

export default {
  components: { ProductDetails },
  props: {
    id: { type: [Number, String], default: () => null },
  },
  mixins: [BreadcrumbMixin],
  data() {
    return {
      loading: false,
      data: {},
      skuSelected: {},
      saleTypeSelected: null,
    }
  },
  computed: {
    items() {
      return this.$store.state.cart.carts
    },
  },
  created() {
    if (this.id) {
      this.fetchItem()
    } else {
      this.$router.push({ path: this.$routePage.EC_LINE001 })
    }
  },

  methods: {
    async fetchItem() {
      try {
        this.loading = true

        const { data } = await ProductService.getProductDetail(this.id)

        this.data = {
          ...data,
          product_images: data.product_images.sort((a, b) => a.sort_no - b.sort_no),
        }
      } catch (error) {
        this.$router.push({ name: '404' })
      } finally {
        this.loading = false
      }
    },
    onSubmit(sku) {
      const isExisted = this.items.some((i) => i.id === sku.id && i.subscription === sku.subscription)

      if (isExisted) {
        this.$router.push({ path: this.$routePage.EC_LINE003 })
      } else {
        this.$store
          .dispatch('cart/ADD_CART', {
            ...sku,
            quantity: 1,
            sale_type: this.data.sale_type,
          })
          .then(() => this.$store.commit('cart/SET_SUBSCRIPTION', sku.subscription))
          .then(() =>
            this.$alert({
              message: this.$t('ec-line002.content.addCartSuccess'),
              type: 'success',
            })
          )
      }
    },
    productPrice() {
      const priceBySaleType = () => {
        return {
          [this.Constants.SALE_TYPE.NORMAL]: this.skuSelected.price02_after_tax,
          [this.Constants.SALE_TYPE.SUBSCRIPTION]: this.skuSelected.price03_after_tax,
          [this.Constants.SALE_TYPE.BOTH]:
            this.saleTypeSelected === this.Constants.SALE_TYPE.NORMAL
              ? this.skuSelected.price02_after_tax
              : this.skuSelected.price03_after_tax,
        }
      }

      if (!this.skuSelected) {
        return '0'
      } else {
        return priceBySaleType(this.skuSelected)[this.data.sale_type]
      }
    },
  },
  metaInfo() {
    const product_images = this.data?.product_images?.map((i) =>
      Object.assign({ url: `${this.$store.getters['env/GET_KEY'](ENV_KEY.IMAGE_API)}/${i.file_name}` })
    )

    const product_description = this.data?.description_detail?.replace(/<[^>]+>/g, '')

    const price = this.productPrice()
    return {
      title: this.data.name,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('ec-line002.description', {
            name: this.data.name,
            description: product_description,
          }).slice(0, 120),
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.$t('ec-line002.keyword', { shop_name: this.shopName, name: this.data.name }),
        },
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: this.data.name,
            logo: `${this.$store.getters['env/GET_KEY'](ENV_KEY.BASE_URL)}${this.Constants.SHOP_CONFIG.LOGO_URL}`,
            image: product_images?.map((i) => i.url),
            description: this.data.description_detail,
            brand: {
              '@type': 'Brand',
              name: this.$store.getters['auth/getShopName'],
            },
            offers: {
              '@type': 'Offer',
              priceCurrency: 'JPY',
              price: price,
            },
          },
        },
      ],
    }
  },
}
</script>
